import {
  AppBar,
  Avatar,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop, useIsWebView } from 'Hooks';
import {
  MobileMenu as MobileMenuIcon,
  NewLogo,
  NewLogoMobile,
  OutlinedBluePhone,
  SearchIcon,
} from 'Images';
import { UserAccordion } from './UserAccordion';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { MobileMenu } from './MobileMenu';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { LawyerDialog } from 'Components/LayerDialog';
import { getIsLawyer, getIsLawyerAndUser } from 'Utils/lawyers';
import { ActivitiesSearchBar } from 'Components/ActivitiesSearchBar';
import { useIsLawyerArea } from 'Hooks/useIsLawyerArea';

export const Header2 = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Header);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const isLawyerArea = useIsLawyerArea();
  const isWebView = useIsWebView();

  if (isWebView) return null;

  return (
    <Stack
      sx={{
        height: isDesktop ? '80px' : '72px',
      }}
    >
      <AppBar
        sx={{
          height: isDesktop ? '80px' : '72px',
          boxShadow: 'none',
          zIndex: 1300,
        }}
      >
        <Stack
          direction={'row'}
          sx={{
            justifyContent: isDesktop ? 'space-between' : 'center',
            gap: isDesktop ? '0' : '50px',
            padding: isDesktop ? '16px 40px' : '20px 0px 20px 14px',
            height: '100%',

            bgcolor:
              import.meta.env.VITE_APP_ENV_NAME === 'pp'
                ? '#A020FF'
                : 'text.secondary',
          }}
        >
          <Stack
            direction={'row'}
            sx={{
              alignSelf: 'center',
              position: isDesktop ? 'static' : 'absolute',
              width: 'auto',
              gap: '16px',
              right: 0,
              top: '50%',
              transform: isDesktop ? undefined : 'translateY(-50%)',
            }}
          >
            {isDesktop ? (
              <>
                <Stack direction={'row'}>
                  <Stack
                    width={'auto'}
                    alignItems={'center'}
                    sx={{ cursor: 'pointer', alignSelf: 'center' }}
                    onClick={() => {
                      window.location.href = `${
                        import.meta.env.VITE_APP_MARKETING_SITE_URL
                      }`;
                    }}
                    onKeyDown={onEnterOrEscapeKeyDown}
                    tabIndex={0}
                    component='a'
                  >
                    <Avatar
                      src={isDesktop ? NewLogo : NewLogoMobile}
                      alt={''}
                      sx={{
                        height: '46px',
                        width: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Stack>
                </Stack>
                {import.meta.env.VITE_APP_ENV_NAME === 'pp' && (
                  <Typography
                    sx={{
                      fontSize: '40px !important',
                      fontWeight: '700 !important',
                      width: 'max-content',
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%) ',
                    }}
                  >
                    {dictionary.preProdText}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Button
                  sx={{ padding: '0', minWidth: '0' }}
                  tabIndex={0}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label={dictionary.mobileMenuAriaControl}
                >
                  <Avatar
                    alt={''}
                    src={MobileMenuIcon}
                    sx={{ width: '52px', height: '48px' }}
                  />
                </Button>
                {isMobileMenuOpen && (
                  <MobileMenu
                    isMobileMenuOpen={isMobileMenuOpen}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                    showDialog={() => {
                      setIsMobileMenuOpen(false);
                      setShowDialog(true);
                    }}
                  />
                )}
                <Stack
                  width={'auto'}
                  alignItems={'center'}
                  sx={{ alignSelf: 'center' }}
                >
                  <Avatar
                    variant={'square'}
                    src={NewLogoMobile}
                    alt={''}
                    sx={{
                      height: 32,
                      width: (32 * 240) / 56,
                      '& img': {
                        objectFit: 'contain',
                      },
                    }}
                  />
                </Stack>
                {import.meta.env.VITE_APP_ENV_NAME === 'pp' && (
                  <Typography
                    sx={{
                      fontSize: '32px !important',
                      fontWeight: '700 !important',
                      width: 'max-content',
                      position: 'absolute',
                      left: 'calc(100% + 10px)',
                      top: '12px',
                    }}
                  >
                    {dictionary.preProdTextMobile}
                  </Typography>
                )}
              </>
            )}
            <LawyerDialog
              showDialog={showDialog}
              setShowDialog={setShowDialog}
            />
          </Stack>
          {(!getIsLawyer() || (getIsLawyerAndUser() && !isLawyerArea)) && (
            <Stack
              direction={'row'}
              sx={{
                alignSelf: isDesktop ? 'center' : 'initial',
                margin: isDesktop ? '0' : 'auto auto auto 0',
                width: 'auto',
                gap: isDesktop ? '16px' : '12px',
                height: isDesktop ? 'auto' : '100%',
              }}
            >
              <IconButton
                aria-label={dictionary.search}
                onClick={() => setIsSearchOpen((prev) => !prev)}
                sx={{
                  padding: '0',
                  width: isDesktop ? '44px' : '30px',
                  height: isDesktop ? '44px' : '30px',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Avatar
                  alt=''
                  src={SearchIcon}
                  sx={{
                    width: isDesktop ? '44px' : '30px',
                    height: isDesktop ? '44px' : '30px',
                  }}
                />
              </IconButton>
              <Button
                aria-label={dictionary.myTeam}
                sx={{
                  padding: '0',
                  minWidth: '0',
                  bgcolor: '#fff',
                  gap: '5px',
                  borderRadius: '8px',
                  width: isDesktop ? '129px' : '30px',
                  height: isDesktop ? 'auto' : '30px',
                  '&:hover': {
                    bgcolor: '#fff',
                  },
                }}
                onClick={() => navigate('/personal-team')}
              >
                <Avatar
                  alt=''
                  src={OutlinedBluePhone}
                  sx={{
                    width: isDesktop ? '28px' : '20px',
                    height: isDesktop ? '28px' : '20px',
                  }}
                />
                {isDesktop && (
                  <Typography variant={'body5'}>{dictionary.myTeam}</Typography>
                )}
              </Button>
              <UserAccordion />
            </Stack>
          )}
        </Stack>
      </AppBar>
      {(!getIsLawyer() || (getIsLawyerAndUser() && !isLawyerArea)) && (
        <ActivitiesSearchBar
          initialOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          source='header'
        />
      )}
    </Stack>
  );
};
